<template>
  <!-- setting `--progress` manually for proper animation on page load -->
  <!-- prettier-ignore-attribute style -->
  <div class="pricing-tariffs-slider-outer" ref="outer" style="--progress:0;">
    <!-- prettier-ignore-attribute style -->
    <div class="pricing-tariffs-slider-inner" ref="inner" style="--translate:0px;">
      <div class="pricing-tariffs-slider" ref="slider">
        <div class="swiper" ref="swiper">
          <div class="swiper-wrapper">
            <template v-for="(tariff, i) in tariffs">
              <div
                v-if="tariff !== 'small'"
                class="swiper-slide"
                :data-index="i"
                ref="swiperSlide"
                @click="handleClick"
              >
                <div class="pricing-tariffs-card-wrapper">
                  <PricingTariffsCard
                    :key="`card-${i}`"
                    :currency="currency"
                    :lang="lang"
                    :tariff="tariff"
                  ></PricingTariffsCard>
                  <pricing-tariffs-features-slider
                    :key="`slider-${i}`"
                    :enabled="false"
                    :index="i"
                    :tariff="tariff"
                    :activeIndex="swiperData.activeIndex"
                    :resetSlider="resetFeaturesSlider"
                  ></pricing-tariffs-features-slider>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import eases from 'eases';
import scrollTo from '~scripts/core/scrollTo';
import translations from './pricing-translations';
import PricingTariffsCard from './PricingTariffsCard.vue';
import PricingTariffsFeaturesSlider from '../PricingTariffsFeatures/PricingTariffsFeaturesSlider.vue';

const ANIMATION_DATA = { speed: 200 };
// TODO: Get rid of duplication of this data in CSS and JS: pass them to CSS as custom properties
const SWIPER_SIZES = { slideWidth: { lg: 700, xxl: 800 }, spaceBetween: { lg: 10, xxl: 20 } };

export default {
  name: 'PricingTariffsSlider',
  components: { PricingTariffsCard, PricingTariffsFeaturesSlider },
  props: {
    currency: {
      type: String,
      required: true,
    },
    lang: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      animation: undefined,
      animationStart: 0,
      animationEnd: 0,
      swiper: undefined,
      swiperData: {
        activeIndex: 0,
        translate: 0,
      },
      swiperSizes: {
        slideWidth: 0,
        spaceBetween: 0,
      },
      progress: 0,
      resetFeaturesSlider: false,
    };
  },
  computed: {
    breakpointXxlMin() {
      return BREAKPOINTS.xxlMin;
    },
    mediaXxl() {
      return window.matchMedia(`(min-width: ${this.breakpointXxlMin})`);
    },
    mediaXxlMinNumber() {
      return parseInt(this.breakpointXxlMin);
    },
    translations() {
      return translations[this.lang];
    },
    tariffs() {
      return Object.keys(this.translations.tariffs);
    },
    pricingTariffsWrapperEl() {
      return document.getElementById('pricing-tariffs-wrapper');
    },
    pricingTariffsEl() {
      return document.getElementById('pricing-tariffs');
    },
    offerWrapperEl() {
      return document.getElementById('offer-wrapper');
    },
  },
  mounted() {
    this.setAnimationStart();
    this.setAnimationEnd();
    this.handleAnimationStart();

    this.setProgress(0);
    this.handleSwiperSizesInitial();
    this.setInitialSlide();

    this.mediaXxl.addEventListener('change', this.handleMediaXxlMatchesChange, { passive: true });

    window.addEventListener('resize', this.handleResize, { passive: true });

    // Called from `static/promo-static/landing-2021/src/scripts/pages/price/index/index.js`
    this.$root.$on('scrollToTop', () => {
      const { progress } = this;

      if (progress > 0 && progress < 1) {
        scrollTo(0, { duration: ANIMATION_DATA.speed, easing: eases.linear });
      }
    });
  },
  beforeDestroy() {
    this.mediaXxl.removeEventListener('change', this.handleMediaXxlMatchesChange);

    window.removeEventListener('resize', this.handleResize);

    this.handleAnimationStop();

    this.setProgress(0, false);
  },
  methods: {
    handleSwiperSizesInitial() {
      const { swiperSizes } = this;

      if (this.mediaXxl.matches) {
        swiperSizes.slideWidth = SWIPER_SIZES.slideWidth.xxl;
        swiperSizes.spaceBetween = SWIPER_SIZES.spaceBetween.xxl;
      } else {
        swiperSizes.slideWidth = SWIPER_SIZES.slideWidth.lg;
        swiperSizes.spaceBetween = SWIPER_SIZES.spaceBetween.lg;
      }

      this.setInitialSlide(this.swiperData.activeIndex);
    },
    handleMediaXxlMatchesChange() {
      this.handleSwiperSizesInitial();
    },
    swiperCreate() {
      if (!this.swiper) {
        this.swiper = new Swiper(this.$refs.swiper, {
          allowTouchMove: true,
          breakpoints: {
            // when window width is >= this.mediaXxlMinNumber
            [this.mediaXxlMinNumber]: {
              spaceBetween: SWIPER_SIZES.spaceBetween.xxl,
            },
          },
          effect: 'slide',
          init: false,
          loop: false,
          roundLengths: true,
          slidesPerView: 'auto',
          // Works incorrect with Apple touchpad force touch,
          // using swiper.slideTo(index) method on click instead
          // slideToClickedSlide: true,
          spaceBetween: SWIPER_SIZES.spaceBetween.lg,
          speed: ANIMATION_DATA.speed,
        });

        // To restore previously active slide
        this.swiper.on('slideChangeTransitionEnd', () => {
          this.setInitialSlide(this.swiperData.activeIndex);
        });

        this.swiper.on('touchMove', (swiper) => {
          // Using `classList.add` instead of `swiper.setGrabCursor()`,
          // because `.setGrabCursor()` sets `cursor: grab;` on `.swiper-wrapper` inline styling,
          // and we already have `cursor: pointer;` on inner element `.swiper-slide`.
          swiper.el.classList.add('grab');
        });
        this.swiper.on('touchEnd', (swiper) => {
          // swiper.unsetGrabCursor();
          swiper.el.classList.remove('grab');
        });
      }
    },
    swiperDestroy() {
      if (this.swiper && this.swiper.initialized) {
        this.swiper.destroy();
        this.swiper = undefined;
      }
    },
    handleAnimationStart() {
      const { animationStart, animationEnd } = this;

      this.animation = scrollAnimationProgress(this.$refs.outer, {
        getStart() {
          return animationStart;
        },
        getEnd(el, start) {
          return start + animationEnd;
        },
        lerpValue: 0.6,
        onProgress: (progress) => {
          const { swiper } = this;
          const slideActive = this.$refs.swiperSlide[this.swiperData.activeIndex];

          this.progress = progress;

          if (!swiper) {
            this.swiperCreate();
          }

          if (!slideActive.classList.contains('swiper-slide-active')) {
            slideActive.classList.add('swiper-slide-active');
          }

          if (progress === 0) {
            // Reset features sliders to the first slide
            this.resetFeaturesSlider = !this.resetFeaturesSlider;
          } else if (progress > 0 && progress < 1) {
            if (swiper?.initialized) {
              this.swiperDestroy();
            }
          } else if (progress === 1) {
            if (!swiper?.initialized) {
              swiper.init();

              // To restore previously active slide
              swiper.setTranslate(this.swiperData.translate);
              swiper.slideTo(this.swiperData.activeIndex, 0, false);
            }
          }

          this.setProgress(progress);
        },
      });
    },
    handleAnimationStop() {
      let { animation } = this;

      if (animation && typeof animation === 'function') {
        animation();
      }

      animation = undefined;

      this.swiperDestroy();
    },
    setAnimationStart() {
      const { offerWrapperEl } = this;

      if (offerWrapperEl) {
        this.animationStart = Math.round(
          window.scrollY + offerWrapperEl.getBoundingClientRect().top + offerWrapperEl.offsetHeight - this.$refs.outer.offsetHeight / 2
        );
      } else {
        this.animationStart = 0;
      }
    },
    setAnimationEnd() {
      this.animationEnd = this.$refs.outer.offsetHeight / 3;
    },
    handleClick(e) {
      const { progress } = this;
      const index = parseInt(e.target.closest('.swiper-slide').dataset.index);
      const scrollToY = this.animationStart + this.animationEnd + 1;

      this.setInitialSlide(index);

      if (progress < 1) {
        scrollTo(scrollToY, { duration: ANIMATION_DATA.speed, easing: eases.linear });
      } else if (progress === 1) {
        this.swiper.slideTo(index);
      }
    },
    handleResize() {
      this.handleAnimationStop();
      this.setAnimationStart();
      this.setAnimationEnd();
      this.handleAnimationStart();
    },
    setProgress(progress, set = true) {
      const { pricingTariffsWrapperEl } = this;

      if (set) {
        pricingTariffsWrapperEl.style.setProperty('--progress', progress);
      } else {
        pricingTariffsWrapperEl.style.removeProperty('--progress');
      }
    },
    setInitialSlide(index = 1) { // Opening "Maximum" by default
      const { swiperData, swiperSizes } = this;
      const { inner } = this.$refs;
      const size = -Math.abs(swiperSizes.slideWidth + swiperSizes.spaceBetween);

      swiperData.activeIndex = index;
      swiperData.translate = size * index;
      inner.style.setProperty('--translate', `${size * index}px`);
    },
  },
};
</script>
